"use client";

import { useEffect, useRef, useState } from "react";
import { Coins } from "lucide-react";
import { io, type Socket } from "socket.io-client";
import { useTranslation } from "react-i18next";
import { api, type OrderbookSide } from "@/lib/api";
import { formatShares } from "@/lib/format";

interface OrderBookViewProps {
  slug: string;
  // EPIC-027 STORY-697 — when set, reward-zone tinted band renders
  // around midpoint ± maxSpreadCents and price levels within the
  // band carry an "earning rewards" indicator. Falsy values disable
  // the visual entirely.
  rewardsEnabled?: boolean;
  maxSpreadCents?: number | null;
}

type Book = Awaited<ReturnType<typeof api.getOrderbook>>;

interface IncomingTradeEvent {
  marketSlug: string;
}

/**
 * STORY-308 phase 2 follow-up — orderbook auto-refreshes when a
 * `market:trade` event fires for this slug, so depth updates without
 * a page navigation. Polymarket / Kalshi parity item.
 *
 * Implementation choice: refetch the full orderbook (cheap GET) on
 * each trade event rather than maintaining a delta-applied local
 * book. Closed-beta trade volumes don't justify the complexity of
 * a stateful CRDT, and a simple refetch eliminates an entire class
 * of "client diverges from server" bugs.
 *
 * 200 ms debounce coalesces back-to-back fills (matcher can emit
 * several trades in one tx) into one fetch.
 *
 * Self-heal on 429 / network errors lives below in fetchBook (PR #728);
 * exponential backoff 4s → 60s ceiling. This block is a no-op marker
 * to bust Vercel's stale build cache after the previous deploy of
 * #728 produced a chunk that didn't include the retry code.
 */
export function OrderBookView({
  slug,
  rewardsEnabled,
  maxSpreadCents,
}: OrderBookViewProps) {
  const { t } = useTranslation();
  const [book, setBook] = useState<Book | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [flashing, setFlashing] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Pin the load-failure fallback at mount so the effect's dep
  // array doesn't have to re-run on locale change. The error is
  // only visible until the user retries — staleness here is fine.
  const loadFailedFallback = t("orderBook.loadFailed");

  // Initial load + every refetch share this fn.
  useEffect(() => {
    let cancelled = false;
    let socket: Socket | null = null;
    // 2026-04-30 — auto-retry on error. The orderbook is event-driven
    // (refetched on market:trade / market:orderbook socket events), so
    // a quiet market with no trades + an initial fetch failure leaves
    // the user staring at "Order book unavailable" forever. With
    // @nestjs/throttler short-window 429s during deploys or under
    // perf-test load, this regression was visible on 2026-04-30 right
    // after the SkipThrottle fix shipped — the API recovered but the
    // component never re-tried. Retry every 4s with exponential backoff
    // (capped at 60s) until a fetch succeeds, then back off entirely.
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let consecutiveFailures = 0;

    async function fetchBook() {
      try {
        const next = await api.getOrderbook(slug);
        if (!cancelled) {
          setBook(next);
          setError(null);
          consecutiveFailures = 0;
          if (retryTimer) {
            clearTimeout(retryTimer);
            retryTimer = null;
          }
          setFlashing(true);
          setTimeout(() => {
            if (!cancelled) setFlashing(false);
          }, 600);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : loadFailedFallback);
          consecutiveFailures += 1;
          // 4s · 8s · 16s · 32s · 60s ceiling. Caps at 60s so a long
          // outage doesn't burn battery; resumes on the next event or
          // socket reconnect.
          const backoffMs = Math.min(60_000, 4_000 * 2 ** (consecutiveFailures - 1));
          if (retryTimer) clearTimeout(retryTimer);
          retryTimer = setTimeout(() => {
            if (!cancelled) void fetchBook();
          }, backoffMs);
        }
      }
    }

    void fetchBook();

    const apiBase = process.env.NEXT_PUBLIC_API_URL;
    if (apiBase) {
      const url = apiBase.replace(/\/$/, "");
      socket = io(url, {
        transports: ["websocket", "polling"],
        reconnection: true,
        reconnectionAttempts: 5,
      });
      socket.on("connect", () => {
        socket?.emit("subscribe", { slug });
      });
      const scheduleRefetch = () => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => void fetchBook(), 200);
      };
      socket.on("market:trade", (event: IncomingTradeEvent) => {
        if (event.marketSlug !== slug) return;
        // Debounce — multiple trades from one matcher tx coalesce.
        scheduleRefetch();
      });
      // STORY-347 / STORY-308 — order placements without fills (e.g.
      // a fresh limit, or a market that auto-converts to a resting
      // limit at the anchor) emit market:orderbook but NOT
      // market:trade. Without this listener the book stays stale
      // until the next trade or page reload.
      socket.on("market:orderbook", (event: IncomingTradeEvent) => {
        if (event.marketSlug !== slug) return;
        scheduleRefetch();
      });
    }

    return () => {
      cancelled = true;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (retryTimer) clearTimeout(retryTimer);
      if (socket) {
        socket.emit("unsubscribe", { slug });
        socket.disconnect();
      }
    };
  }, [slug, loadFailedFallback]);

  if (error) {
    return (
      <div
        className="rounded-md border px-3 py-2 text-xs"
        style={{
          borderColor: "color-mix(in srgb, var(--color-status-negative) 30%, transparent)",
          background: "color-mix(in srgb, var(--color-status-negative) 8%, transparent)",
          color: "var(--color-status-negative)",
        }}
      >
        Order book unavailable: {error}
      </div>
    );
  }

  if (!book) {
    return (
      <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-3 text-center text-xs text-[var(--color-text3)]">
        {t("orderBook.loading")}
      </div>
    );
  }

  // EPIC-027 STORY-697 — only render the band when the market is
  // rewards-enabled AND we have a midpoint to anchor it to. Each side
  // (YES / NO) carries its own midpoint, so the band is computed
  // per-card rather than globally.
  const rewardZone =
    rewardsEnabled && maxSpreadCents && maxSpreadCents > 0
      ? maxSpreadCents
      : null;

  return <FocusedOrderBook book={book} flashing={flashing} rewardZone={rewardZone} />;
}

/**
 * 2026-04-30 founder UX pass — render a single focused book at a
 * time (Polymarket / Kalshi pattern) with a YES/NO toggle. Two
 * side-by-side books were the highest-ranked confusion source in
 * the trade-flow audit. Default focus is YES; user can flip to NO
 * with one click. "Both" is available as a power-user fallback for
 * arbitrage-style watching.
 */
function FocusedOrderBook({
  book,
  flashing,
  rewardZone,
}: {
  book: NonNullable<Book>;
  flashing: boolean;
  rewardZone: number | null;
}) {
  const { t } = useTranslation();
  const [focus, setFocus] = useState<"yes" | "no" | "both">("yes");

  return (
    <div className="flex flex-col gap-2">
      <div
        className="inline-flex self-end overflow-hidden rounded-md border border-[var(--color-border)] text-[10px] font-bold uppercase tracking-[0.4px]"
        role="tablist"
        aria-label={t("orderBook.focusToggleAria")}
      >
        {(["yes", "no", "both"] as const).map((opt) => {
          const active = focus === opt;
          const accent =
            opt === "yes"
              ? "var(--color-yes)"
              : opt === "no"
                ? "var(--color-no)"
                : "var(--color-text2)";
          return (
            <button
              key={opt}
              type="button"
              onClick={() => setFocus(opt)}
              className="px-2 py-1 transition-colors"
              style={{
                color: active ? accent : "var(--color-text3)",
                background: active
                  ? `color-mix(in srgb, ${accent} 12%, transparent)`
                  : "transparent",
              }}
              role="tab"
              aria-selected={active}
            >
              {t(`orderBook.focus.${opt}`)}
            </button>
          );
        })}
      </div>
      <div
        className={
          focus === "both"
            ? "grid grid-cols-1 gap-3 sm:grid-cols-2"
            : "grid grid-cols-1 gap-3"
        }
      >
        {(focus === "yes" || focus === "both") && (
          <BookCard
            label={t("orderBook.yesBook")}
            side={book.yes}
            accent="yes"
            flashing={flashing}
            rewardZone={rewardZone}
          />
        )}
        {(focus === "no" || focus === "both") && (
          <BookCard
            label={t("orderBook.noBook")}
            side={book.no}
            accent="no"
            flashing={flashing}
            rewardZone={rewardZone}
          />
        )}
      </div>
    </div>
  );
}

function BookCard({
  label,
  side,
  accent,
  flashing,
  rewardZone,
}: {
  label: string;
  side: OrderbookSide;
  accent: "yes" | "no";
  flashing: boolean;
  rewardZone: number | null;
}) {
  const { t } = useTranslation();
  const accentColor = accent === "yes" ? "var(--color-yes)" : "var(--color-no)";

  // Reward band anchors at the side's midpoint; without a midpoint
  // (no two-sided market) the band can't render.
  const bandLow =
    rewardZone !== null && side.midPrice !== null
      ? Math.max(1, side.midPrice - rewardZone)
      : null;
  const bandHigh =
    rewardZone !== null && side.midPrice !== null
      ? Math.min(99, side.midPrice + rewardZone)
      : null;

  return (
    <div
      className="overflow-hidden rounded-[10px] border bg-[var(--color-surface)] transition-colors duration-500"
      style={{
        borderColor: flashing ? accentColor : "var(--color-border)",
      }}
    >
      <div className="flex items-center justify-between border-b border-[var(--color-border)] px-3 py-2 text-[11px]">
        <span
          className="font-bold uppercase tracking-[0.5px]"
          style={{ color: accentColor }}
        >
          {label}
        </span>
        <span className="font-mono tabular-nums text-[var(--color-text3)]">
          {side.bestBid !== null && side.bestAsk !== null
            ? t("orderBook.spreadSummary", {
                bid: side.bestBid,
                ask: side.bestAsk,
                spread: side.spread,
              })
            : t("orderBook.noOrders")}
        </span>
      </div>

      {bandLow !== null && bandHigh !== null && (
        <div
          className="flex items-center gap-1.5 border-b border-[var(--color-border)] px-3 py-1 text-[9px] font-bold uppercase tracking-[0.4px]"
          style={{
            background: "var(--color-brand-tint)",
            color: "var(--color-brand)",
          }}
          title={t("orderBook.rewardZoneTitle")}
        >
          <Coins size={10} strokeWidth={2} aria-hidden="true" />
          {t("orderBook.rewardZoneLabel")} {bandLow}¢ – {bandHigh}¢
        </div>
      )}

      <div className="grid grid-cols-2 gap-px bg-[var(--color-border)]">
        <BookSide
          direction="bid"
          levels={side.bids}
          bandLow={bandLow}
          bandHigh={bandHigh}
        />
        <BookSide
          direction="ask"
          levels={side.asks}
          bandLow={bandLow}
          bandHigh={bandHigh}
        />
      </div>
    </div>
  );
}

function BookSide({
  direction,
  levels,
  bandLow,
  bandHigh,
}: {
  direction: "bid" | "ask";
  levels: Array<{
    priceCents: number;
    sizeShares: string;
    orderCount: number;
    impliedDepthShares: string;
    ownDepthShares?: string;
  }>;
  bandLow: number | null;
  bandHigh: number | null;
}) {
  const { t } = useTranslation();
  const isBid = direction === "bid";
  const color = isBid ? "var(--color-yes)" : "var(--color-no)";
  const maxDepth = levels.reduce(
    (max, lvl) => Math.max(max, Number(lvl.sizeShares)),
    0,
  );
  // V1 matcher caveat: levels marked "implied" come from opposite-outcome
  // BUY orders displayed via complementary pricing. They look fillable
  // but won't pair-mint against a same-side order under the current
  // matcher. Distinguish visually so users don't expect an immediate
  // fill against an implied level.

  return (
    <div className="bg-[var(--color-surface)]">
      <div className="grid grid-cols-2 px-3 py-1.5 text-[9px] font-bold uppercase tracking-[0.4px] text-[var(--color-text3)]">
        <span>{isBid ? t("orderBook.bid") : t("orderBook.ask")}</span>
        <span className="text-right">{t("orderBook.size")}</span>
      </div>
      {levels.length === 0 ? (
        <div className="px-3 py-3 text-center text-[10px] text-[var(--color-text3)]">
          —
        </div>
      ) : (
        levels.map((lvl, i) => {
          const total = Number(lvl.sizeShares);
          const implied = Number(lvl.impliedDepthShares ?? "0");
          const own = Number(lvl.ownDepthShares ?? "0");
          const real = total - implied;
          const depthPct = maxDepth > 0 ? (total / maxDepth) * 100 : 0;
          const fullyImplied = implied > 0 && real === 0;
          const partlyImplied = implied > 0 && real > 0;
          // STORY-C (#1559) — own-attribution flags. fullyOwn means
          // every share at this level is the viewer's own order;
          // partlyOwn means some shares are theirs alongside other
          // makers'. Used to italicize + tooltip "won't cross
          // against you" so the viewer understands why a same-
          // priced order rests instead of crossing (STP).
          const fullyOwn = own > 0 && own >= total;
          const partlyOwn = own > 0 && own < total;
          const inRewardZone =
            bandLow !== null &&
            bandHigh !== null &&
            lvl.priceCents >= bandLow &&
            lvl.priceCents <= bandHigh;
          return (
            <div
              key={`${lvl.priceCents}-${i}`}
              className={`relative grid grid-cols-2 px-3 py-[3px] font-mono text-[10.5px] tabular-nums ${
                fullyImplied || fullyOwn ? "italic" : ""
              }`}
              style={
                inRewardZone
                  ? { background: "var(--color-brand-tint)" }
                  : fullyOwn
                    ? { color: "var(--color-text2)" }
                    : undefined
              }
              title={
                fullyOwn
                  ? `${own} share${own === 1 ? "" : "s"} at this level are from your own order — won't cross against you (anti-self-trade).`
                  : partlyOwn
                    ? `${own} of ${total} shares at this level are from your own orders — those won't cross against you.`
                    : fullyImplied
                      ? t("orderBook.impliedTitle")
                      : partlyImplied
                        ? t("orderBook.partlyImpliedTitle", { real, implied })
                        : undefined
              }
            >
              <span
                aria-hidden="true"
                className="absolute inset-y-0 opacity-10"
                style={{
                  background: color,
                  width: `${depthPct}%`,
                  ...(isBid ? { right: 0 } : { left: 0 }),
                }}
              />
              {/* 2026-04-30 founder UX pass — hide the "(N impl)" /
                  "impl" badges from default render. The synthesized-
                  liquidity distinction (binary-market same-side flip
                  via 1¢ inversion) is implementation detail; users
                  see one number for total depth. The italic styling
                  on fully-implied levels stays as a subtle hint. */}
              <span
                className="relative font-semibold"
                style={{
                  color,
                  opacity: fullyImplied ? 0.6 : 1,
                }}
              >
                {lvl.priceCents}¢
              </span>
              <span className="relative text-right text-[var(--color-text2)]">
                {/* Migration 0145 — total is centi-shares (1 share = 100). */}
                {formatShares(total)}
              </span>
            </div>
          );
        })
      )}
    </div>
  );
}
